<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { type FieldEntry } from 'vee-validate';
import { type PatientAddressForm } from '@/api/patients';
import { useAddressGeocoding, type GeocodeResult } from '@/composables/useAddressGeocoding';

const { t } = useI18n();
const { attachAutocomplete, openMapPicker } = useAddressGeocoding();

interface FieldArrayMethods {
  push: (value: PatientAddressForm) => void;
  remove: (index: number) => void;
}

interface Props {
  modelValue: FieldEntry<PatientAddressForm>[];
  errors: Record<string, string | undefined>;
  fieldArrayMethods: FieldArrayMethods;
}

const props = defineProps<Props>();

function applyGeocode(entry: FieldEntry<PatientAddressForm>, result: GeocodeResult): void {
  if (result.address) entry.value.address = result.address;
  if (result.latitude) entry.value.latitude = result.latitude;
  if (result.longitude) entry.value.longitude = result.longitude;
}

function blankAddress(primary: boolean): PatientAddressForm {
  return {
    id: undefined,
    label: undefined,
    address: undefined,
    latitude: undefined,
    longitude: undefined,
    locationExtended: false,
    primary,
    _destroy: false,
  };
}

function activeAddresses(): FieldEntry<PatientAddressForm>[] {
  return props.modelValue.filter((entry) => !entry.value._destroy); // eslint-disable-line no-underscore-dangle
}

function setPrimary(index: number): void {
  props.modelValue.forEach((entry, i) => {
    entry.value.primary = i === index;
  });
}

function ensureSinglePrimary(): void {
  const active = activeAddresses();
  if (active.length > 0 && !active.some((entry) => entry.value.primary)) {
    active[0].value.primary = true;
  }
}

function setupAutocomplete(key: number | string): void {
  attachAutocomplete(`patient-address-input-${key}`, (result) => {
    const entry = props.modelValue.find((candidate) => candidate.key === key);
    if (entry) applyGeocode(entry, result);
  });
}

function addAddress(): void {
  const { push } = props.fieldArrayMethods;
  const isFirst = activeAddresses().length === 0;
  push(blankAddress(isFirst));
  nextTick(() => {
    const added = props.modelValue[props.modelValue.length - 1];
    if (added) setupAutocomplete(added.key);
  });
}

function removeAddress(index: number): void {
  const entry = props.modelValue[index];
  if (!entry) return;

  if (entry.value.id) {
    entry.value._destroy = true; // eslint-disable-line no-underscore-dangle
  } else {
    const { remove } = props.fieldArrayMethods;
    remove(index);
  }
  ensureSinglePrimary();
}

function onAddressInput(index: number): void {
  const entry = props.modelValue[index];
  if (!entry) return;
  entry.value.latitude = undefined;
  entry.value.longitude = undefined;
}

// Map dialog (shared, targets one row at a time)
const showMap = ref(false);

async function openMap(index: number): Promise<void> {
  const entry = props.modelValue[index];
  if (!entry?.value.latitude || !entry?.value.longitude) return;

  showMap.value = true;
  await nextTick();
  const position = { lat: parseFloat(entry.value.latitude), lng: parseFloat(entry.value.longitude) };
  await openMapPicker('patient-address-map', position, (result) => applyGeocode(entry, result));
}

function hasErrors(index: number): boolean {
  return Object.keys(props.errors).some(
    (key) => key.startsWith(`patientAddressesAttributes[${index}]`),
  );
}

onMounted(() => {
  ensureSinglePrimary();
  props.modelValue.forEach((entry) => setupAutocomplete(entry.key));
});

defineExpose({ addAddress, removeAddress, setPrimary });
</script>

<template>
  <v-card>
    <v-card-item>
      <v-card-title class="text-subtitle-1 font-weight-bold">
        {{ t('patientForm.sectionAddresses') }}
      </v-card-title>
    </v-card-item>
    <v-card-text class="pt-4">
      <v-alert
        v-if="errors.patientAddressesAttributes"
        type="info"
        variant="tonal"
        class="mb-4"
      >
        {{ errors.patientAddressesAttributes }}
      </v-alert>

      <template
        v-for="(entry, index) in modelValue"
        :key="entry.key"
      >
        <v-card
          v-if="!entry.value._destroy"
          class="mb-4"
          :class="{ 'error-border': hasErrors(index) }"
        >
          <v-card-item>
            <template #prepend>
              <v-icon :color="entry.value.primary ? 'primary' : 'medium-emphasis'">
                mdi-map-marker
              </v-icon>
            </template>
            <v-card-title class="text-body-1 d-flex align-center ga-2">
              <span class="text-truncate">
                {{ entry.value.label || entry.value.address || t('patientForm.newAddress') }}
              </span>
              <v-chip
                v-if="entry.value.primary"
                size="x-small"
                color="primary"
                variant="flat"
              >
                {{ t('patientForm.primaryAddress') }}
              </v-chip>
            </v-card-title>
            <template #append>
              <v-btn
                v-if="!entry.value.primary"
                variant="text"
                size="small"
                color="primary"
                @click="setPrimary(index)"
              >
                {{ t('patientForm.setAsPrimary') }}
              </v-btn>
              <v-btn
                icon="mdi-delete"
                variant="text"
                size="small"
                color="red"
                @click="removeAddress(index)"
              />
            </template>
          </v-card-item>
          <v-card-text class="pt-4">
            <v-text-field
              :id="`patient-address-input-${entry.key}`"
              v-model="entry.value.address"
              variant="outlined"
              prepend-inner-icon="mdi-magnify"
              :label="t('patient.address')"
              :placeholder="t('patientForm.addressSearchPlaceholder')"
              :error-messages="errors[`patientAddressesAttributes[${index}].address`]"
              hide-details="auto"
              @input="onAddressInput(index)"
            >
              <template #append-inner>
                <v-tooltip
                  location="top"
                  :text="t('patientForm.addressMap')"
                >
                  <template #activator="{ props: tooltipProps }">
                    <v-btn
                      v-bind="tooltipProps"
                      icon="mdi-map-search-outline"
                      variant="text"
                      size="small"
                      :disabled="!entry.value.latitude || !entry.value.longitude"
                      @click="openMap(index)"
                    />
                  </template>
                </v-tooltip>
              </template>
            </v-text-field>
            <v-row class="mt-1">
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="entry.value.label"
                  variant="outlined"
                  :label="t('patientForm.addressLabel')"
                  :placeholder="t('patientForm.addressLabelPlaceholder')"
                  persistent-placeholder
                  hide-details="auto"
                  :error-messages="errors[`patientAddressesAttributes[${index}].label`]"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
                class="d-flex align-center"
              >
                <v-switch
                  v-model="entry.value.locationExtended"
                  color="primary"
                  density="compact"
                  hide-details
                  inset
                  :label="t('patientForm.locationExtendedLabel')"
                />
                <v-tooltip
                  location="top"
                  max-width="280"
                  :text="t('patientForm.locationExtended')"
                >
                  <template #activator="{ props: tooltipProps }">
                    <v-icon
                      v-bind="tooltipProps"
                      size="small"
                      color="medium-emphasis"
                      class="ml-2"
                    >
                      mdi-information-outline
                    </v-icon>
                  </template>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </template>

      <v-dialog
        v-model="showMap"
        max-width="800px"
      >
        <v-card class="pa-2">
          <v-card-title>{{ t('patientForm.addressMap') }}</v-card-title>
          <v-card-text>
            <div
              id="patient-address-map"
              style="width: 100%; height: 400px;"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="primary"
              @click="showMap = false"
            >
              Confirmar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <p
        v-if="activeAddresses().length === 0"
        class="text-medium-emphasis text-body-2 mb-4"
      >
        {{ t('patientForm.addressesEmptyHint') }}
      </p>
      <v-btn
        color="primary"
        variant="outlined"
        @click="addAddress"
      >
        {{ t('patientForm.addAddress') }}
      </v-btn>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.error-border {
  border-left: 3px solid rgb(var(--v-theme-error));
  background-color: rgb(var(--v-theme-error), .05);
}
</style>
